import React, { useCallback, useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiLock, FiCheck, FiArrowRight, FiCheckCircle, FiBarChart2 } from 'react-icons/fi';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PATHS } from '@/routes/paths';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { cn, stripDescriptorCode } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ScormQuizCards } from './block/flashcard-quiz/scorm-quiz-cards';
import { learningPathEndpoints } from './block-endpoints';
import { TRAIL_PROGRESS_GRADIENT } from './trilha-list-utils';
import { StatBar, StreakPanel } from './streak-brain';
import { ChapterProgressCard, SectionStepper } from './trail-progress';
import { ChapterCompletedDialog, PerformanceDialog, TrailCompletedDialog } from './chapter-dialogs';
import { TrilhaPerformanceModal } from './trilha-performance-modal';
import { TiaBanner } from '@/components/custom/tia-banner';
import tiaAvatar from '@/components/custom/tia/img/tia-avatar.png';
import tiaTrilhaJornada from '@/assets/images/tia/TIA-TRILHA-JORNADA.png';
import flagIcon from './img/flag.svg';
import trophyIcon from './img/trophy.svg';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const statusLabel = ({ done, skipped, isCurrent, open }) => {
  if (done) return 'Concluído';
  if (skipped) return 'Pulado';
  if (isCurrent) return 'Você está aqui';
  return open ? 'Disponível' : 'Bloqueado';
};

// Card do topo da lista de blocos: só o nome da trilha e o progresso
// (no tema default também o "Dominado", como era no painel do streak).
function TrailProgressCard({ title, walked, mastered, playful, onViewPerformance }) {
  return (
    <div
      style={{ animation: `trilha-in 360ms ${EASE_OUT} both` }}
      className="mb-4 flex flex-col gap-4 rounded-[20px] border border-neutral-100 bg-white p-5 shadow-card"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-2xl font-extrabold leading-tight text-[#222731] sm:text-[28px]">{title}</h2>
        <button
          type="button"
          aria-label="Desempenho na trilha"
          onClick={onViewPerformance}
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-transform duration-100 active:scale-95',
            playful ? 'border-[var(--color-mission,#F99153)] bg-white text-[var(--color-mission,#F99153)]' : 'border-neutral-lightest bg-white text-primary hover:bg-neutral-50'
          )}
        >
          <FiBarChart2 className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-col gap-3">
        <StatBar
          value={walked}
          label="Percorrido"
          color={TRAIL_PROGRESS_GRADIENT}
        />
        {!playful && (
          <StatBar
            value={mastered}
            label="Dominado"
            color="var(--color-success)"
          />
        )}
      </div>
    </div>
  );
}

// POST /trails/:id/:chapter_id/next: o jsonapi-react descarta o `state` do corpo, então o
// `no_content_left` (não sobrou conteúdo: a trilha foi encerrada) é reconhecido pela mensagem do 422.
const NO_CONTENT_LEFT_DETAIL = 'Não há mais conteúdo disponível na trilha';

const nextChapterOutcome = response => {
  if (response?.data) return { chapter: response.data };
  if (response?.errors?.some(e => e.detail === NO_CONTENT_LEFT_DETAIL)) return { finished: true };
  if (response?.error?.status === '409') return { notLatest: true };
  return { failed: true };
};

// Mensagem de incentivo da T.I.A conforme o estado da trilha.
const encourage = ({ mastered, streakCount, walked }) => {
  if (mastered >= 100) return 'Você dominou a trilha! Que orgulho. 🎉';
  if (streakCount >= 5) return `Você está há ${streakCount} dias seguidos. Imparável! 🔥`;
  if (streakCount >= 1) return `${streakCount} ${streakCount === 1 ? 'dia' : 'dias'} de ofensiva! Continue firme. 💪`;
  if (walked > 0) return 'Bom ritmo! Cada bloco te deixa mais perto. Bora continuar?';
  return 'Bora começar? Vou te acompanhar em cada bloco da sua trilha. 💙';
};

export function LearningPathView() {
  const client = useClient();
  const navigate = useNavigate();
  const location = useLocation();
  const playful = usePlayfulStudent();
  const { trailId, chapterId } = useParams();
  const [trilha, setTrilha] = useState(null);
  const [loading, setLoading] = useState(true);
  // Checkpoint de revisão abre num modal SOBRE a trilha (não navega): null | 'loading' | { blockId, sectionId, cards }
  const [reviewState, setReviewState] = useState(null);
  // Fim de capítulo (só personalizada): 'completed' → 'performance' → próximo capítulo.
  // Fim de trilha ('trail-completed'): fixa concluída, ou personalizada sem conteúdo para o próximo capítulo.
  // null | 'completed' | 'performance' | 'trail-completed'
  const [chapterDialog, setChapterDialog] = useState(null);
  const [performance, setPerformance] = useState(null); // attributes de GET user_learning_paths/:id/performance
  const [nextChapter, setNextChapter] = useState(null); // detalhe do capítulo gerado pelo POST .../next
  const [advancing, setAdvancing] = useState(false); // "Próximo capítulo" aguardando a geração/o desempenho
  const [viewingPerformance, setViewingPerformance] = useState(false); // modal "Desempenho na Trilha" aberto

  // Capítulo concluído com próximo a gerar (fim da revisão ou volta a um capítulo já
  // concluído): abre o modal de capítulo concluído (a geração só acontece no clique).
  // Trilha fixa concluída (o último bloco é concluído no player, que volta para cá): fim de trilha.
  const showChapter = useCallback(chapter => {
    setTrilha(chapter);
    setLoading(false);
    if (chapter?.['can-generate-next']) {
      setChapterDialog('completed');
    } else if (chapter && chapter.kind !== 'adaptive' && chapter.status === 'completed') {
      setChapterDialog('trail-completed');
    }
  }, []);

  const fetchChapter = useCallback(async () => {
    const response = await client.fetch(`trails/${trailId}/${chapterId}`);
    showChapter(response?.data || null);
  }, [client, trailId, chapterId, showChapter]);

  useEffect(() => {
    setChapterDialog(null);
    setNextChapter(null);
    setPerformance(null);
    // A lista de trilhas já busca o capítulo antes de navegar e o entrega pelo `state`. O `state`
    // é limpo logo depois: recarregar a página (o histórico guarda o `state`) busca de novo.
    const preloaded = location.state?.chapter;
    if (preloaded && String(preloaded.id) === String(chapterId)) {
      showChapter(preloaded);
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    setLoading(true);
    fetchChapter();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- o `state` só vale na chegada à rota
  }, [fetchChapter]);

  // "Próximo capítulo": gera o próximo capítulo e busca o desempenho do concluído, em paralelo.
  // Sem conteúdo para gerar, a trilha acabou: vai direto ao fim de trilha (sem o modal de desempenho,
  // que anuncia o próximo capítulo); do desempenho só usa os capítulos, para os totais da trilha.
  // O POST é idempotente (200 com o capítulo já gerado), então tentar de novo após erro é seguro.
  const goToNextChapter = async () => {
    setAdvancing(true);
    const [outcome, response] = await Promise.all([
      client
        .mutate(`trails/${trilha['trail-id']}/${trilha.id}/next`, null, { method: 'POST', invalidate: false })
        .then(nextChapterOutcome)
        .catch(() => ({ failed: true })),
      client.fetch(`user_learning_paths/${trilha.id}/performance`).catch(() => null)
    ]);

    if (outcome.finished) {
      setAdvancing(false);
      if (response?.data?.chapters) setPerformance(response.data);
      setChapterDialog('trail-completed');
      return;
    }
    if (outcome.notLatest) {
      // Outro capítulo já é o último (outra aba): vai para ele.
      const latest = await client.fetch(`trails/${trilha['trail-id']}/latest`);
      setAdvancing(false);
      if (latest?.data) navigate(PATHS.LEARNING_PATH.CHAPTER(trilha['trail-id'], latest.data.id));
      return;
    }
    if (outcome.failed) {
      setAdvancing(false);
      toast.error('Não foi possível gerar o próximo capítulo.');
      return;
    }

    setAdvancing(false);
    if (response?.error || response?.errors || !response?.data) {
      toast.error('Não foi possível carregar seu desempenho.');
      return;
    }
    setNextChapter(outcome.chapter);
    setPerformance(response.data);
    setChapterDialog('performance');
  };

  const chapterBreadcrumbs = trailName => ({
    title: 'Aprendizado Dinâmico',
    nav: [
      { route: PATHS.LEARNING_PATH.ROOT, name: 'Trilha', isActive: false },
      {
        route: PATHS.LEARNING_PATH.CHAPTER(trailId, chapterId),
        name: trailName || 'Minha trilha',
        isActive: true
      }
    ]
  });

  const startNextChapter = () => {
    setChapterDialog(null);
    navigate(PATHS.LEARNING_PATH.CHAPTER(nextChapter['trail-id'], nextChapter.id));
  };

  // Carregando: o layout já aparece (sem tela em branco) com o aviso no lugar do conteúdo.
  if (loading) {
    return (
      <DashboardContent breadcrumbs={chapterBreadcrumbs()}>
        <div className="flex w-full flex-col items-center gap-3 py-24 text-center">
          <p className="mb-0 text-base font-bold leading-tight text-neutral-darker">Carregando sua trilha...</p>
          <Loader />
        </div>
      </DashboardContent>
    );
  }
  if (!trilha) {
    return (
      <Navigate
        to={PATHS.LEARNING_PATH.ROOT}
        replace
      />
    );
  }

  const blocks = trilha['blocks'] || [];
  const currentIndex = blocks.findIndex(b => b.state === 'pending');
  const walked = trilha['percent-walked'] ?? 0;
  // Personalizada avança por capítulos/missões; fixa, por seções/passos.
  const personalized = trilha.kind === 'adaptive';
  const mastered = trilha['percent-mastered'] ?? 0;
  // Sequência quebrada (`active: false`) não conta como dias seguidos.
  const streakCount = trilha['streak']?.active ? (trilha['streak']['current-count'] ?? 0) : 0;
  const tiaMessage = encourage({ mastered, streakCount, walked });
  // "ENEM · Capítulo 2" (personalizada) / "ENEM 2026 · Seção 2 · Ciências Humanas..." (fixa com seções).
  const trailName = trilha['display-name'] || trilha['learning-path-name'];
  const trailTitle = personalized ? `${trailName} · Capítulo ${trilha.chapter}` : trilha['stage-current'] ? `${trailName} · Seção ${trilha['stage-current']} · ${trilha['stage-current-label']}` : trailName;

  // Unlock rule (spec §6): done/skipped blocks are always open;
  // the first pending block is the "current" (open); all subsequent pending blocks are locked.
  const isOpen = index => {
    const block = blocks[index];
    if (block.state === 'done' || block.state === 'skipped') return true;
    if (currentIndex === -1) return true; // no pending blocks — all open
    return index === currentIndex;
  };

  // Bloco de revisão abre o quiz num modal aqui mesmo; bloco de conteúdo navega pro player.
  const handleBlockClick = block => {
    if (block.kind === 'review') openReview(block);
    else navigate(PATHS.LEARNING_PATH.BLOCK(trailId, chapterId, block.id));
  };

  const openReview = async block => {
    setReviewState('loading');
    const res = await client.fetch(learningPathEndpoints(block.id).sections);
    const section = (res.data || []).find(s => s.scorm?.scorm_type === 'quiz_cards');
    if (!section) return setReviewState(null);
    setReviewState({ blockId: block.id, sectionId: section.id, cards: section.scorm.content?.cards || [] });
  };

  const markReviewSectionDone = (sectionId, cardResults) => client.mutate(learningPathEndpoints(reviewState.blockId).sectionDone(sectionId), { done: true, card_results: cardResults }, { method: 'put' });

  // Fim da revisão: recarrega o capítulo; se ele foi concluído, o fetch abre o modal de fim de capítulo.
  const finishReview = async () => {
    setReviewState(null);
    setLoading(true);
    await fetchChapter();
  };

  const canGenerateNext = trilha['can-generate-next'];
  // Fim de trilha. Na personalizada, pontos e passos do capítulo aberto não são os da trilha: soma os
  // de cada capítulo (do desempenho buscado no "Próximo capítulo"; sem ele, os do detalhe do capítulo).
  const trailChapters = performance?.chapters || trilha.chapters || [];
  const sumOf = key => trailChapters.reduce((total, c) => total + (Number(c[key]) || 0), 0);
  const trailCompleted = personalized
    ? { trailName, unit: 'chapter', unitsDone: trailChapters.length || trilha.chapter, steps: sumOf('blocks-done'), points: sumOf('points-awarded') }
    : { trailName, unit: 'section', unitsDone: trilha['stage-total'] ?? null, steps: blocks.filter(b => b.state === 'done').length, points: trilha['points-awarded'] ?? 0 };
  const chapterCompleted = {
    chapter: trilha.chapter,
    missionsDone: blocks.filter(b => b.state === 'done').length,
    missionsTotal: blocks.length,
    points: trilha['points-awarded'] ?? 0
  };

  return (
    <DashboardContent breadcrumbs={chapterBreadcrumbs(trailName)}>
      <div className={playful ? 'w-full' : 'mx-auto w-full max-w-5xl'}>
        {playful && (
          // Largura toda (como no Figma): a barra de seções da trilha fixa fica
          // logo abaixo e o card de streak alinha com o card de progresso.
          <TiaBanner
            className="mb-8 md:mt-24"
            image={tiaTrilhaJornada}
            imageHeight={180}
            imageOffset={150}
            bubbleMaxWidth={800}
            text={tiaMessage}
          />
        )}
        {!playful && (
          <div className="mb-7 flex items-start gap-3">
            <img
              src={tiaAvatar}
              alt="T.I.A"
              className="h-12 w-12 shrink-0 rounded-full border-2 border-white object-cover shadow-card"
            />
            <div className="max-w-sm rounded-[4px] rounded-tl-none bg-white p-3 shadow-card">
              <span
                className="block text-[10px] font-extrabold"
                style={{ color: 'var(--color-secondary-dark)' }}
              >
                T.I.A
              </span>
              <p
                className="text-xs font-light leading-relaxed"
                style={{ color: '#222731' }}
              >
                {tiaMessage}
              </p>
            </div>
          </div>
        )}

        {/* Trilha fixa gerada antes das seções vem com `stage-total` null: sem barra de seções. */}
        {!personalized && trilha['stage-total'] > 0 && (
          <SectionStepper
            className="mb-6"
            total={trilha['stage-total']}
            current={trilha['stage-current']}
            currentLabel={trilha['stage-current-label']}
          />
        )}

        <div className="flex w-full flex-col gap-8 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            <TrailProgressCard
              title={trailTitle}
              walked={walked}
              mastered={mastered}
              playful={playful}
              onViewPerformance={() => setViewingPerformance(true)}
            />

            {blocks.length === 0 ? (
              <div
                style={{ animation: `trilha-in 380ms ${EASE_OUT} both` }}
                className="rounded-2xl border border-neutral-100 bg-white p-10 text-center shadow-card"
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-done text-success">
                  <FiCheckCircle className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-neutral-900">Você está em dia! 🎯</h3>
                <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-neutral-500">Não há conteúdo novo na sua trilha agora. Gere uma nova sempre que quiser revisar suas lacunas.</p>
                <Button
                  variant="primary"
                  className="mt-6"
                  onClick={() => navigate(PATHS.LEARNING_PATH.ROOT)}
                >
                  Voltar ao início
                </Button>
              </div>
            ) : (
              <>
                {/* Largada — bandeira quadriculada do legado */}
                <div className={cn('mb-4 flex w-fit items-center gap-4', playful && 'rounded-[20px] border border-neutral-100 bg-white p-3 shadow-card')}>
                  <span className="flex w-12 shrink-0 justify-center">
                    <img
                      src={flagIcon}
                      alt=""
                      className="h-7 w-7"
                    />
                  </span>
                  <span className={cn('text-sm font-bold', playful ? 'text-gray-700' : 'text-primary-dark')}>Largada! Sua jornada começa aqui.</span>
                </div>

                <ol className="relative">
                  {/* Caminho curvo (desktop): liga o centro de cada nó ao próximo em zigzag.
                    Linhas com altura fixa (160px) → posições previsíveis. Nó par x=132, ímpar x=32. */}
                  {playful && blocks.length > 1 && (
                    <svg
                      aria-hidden="true"
                      width="220"
                      height={(blocks.length - 1) * 160 + 80}
                      className="pointer-events-none absolute left-0 top-0 z-0 hidden lg:block"
                    >
                      {blocks.slice(0, -1).map((block, i) => {
                        const x0 = i % 2 === 0 ? 140 : 40;
                        const x1 = (i + 1) % 2 === 0 ? 140 : 40;
                        const y0 = i * 160 + 40;
                        const y1 = (i + 1) * 160 + 40;
                        const midY = (y0 + y1) / 2;
                        return (
                          <path
                            key={block.id}
                            d={`M ${x0} ${y0} C ${x0} ${midY}, ${x1} ${midY}, ${x1} ${y1}`}
                            fill="none"
                            stroke={block.state === 'done' ? '#27AE60' : '#CFC7E6'}
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeDasharray="9 11"
                          />
                        );
                      })}
                    </svg>
                  )}
                  {blocks.map((block, index) => {
                    const open = isOpen(index);
                    const isCurrent = index === currentIndex;
                    const done = block.state === 'done';
                    const skipped = block.state === 'skipped';
                    const last = index === blocks.length - 1;
                    const isReview = block.kind === 'review';
                    // Relevo 3D/chanfrado do nó (lip colorido por estado) — só no tema lúdico.
                    const nodeLip = !open
                      ? '0 3px 0 #CFC9E0'
                      : done
                        ? '0 4px 0 #1E8049, 0 5px 10px rgba(0,0,0,0.16)'
                        : isCurrent
                          ? '0 4px 0 color-mix(in srgb, var(--color-mission) 58%, #000), 0 5px 10px rgba(0,0,0,0.18)'
                          : '0 3px 0 #DCD7EA, 0 4px 8px rgba(0,0,0,0.06)';
                    // Matéria (taxonomia pai do descritor) + tópico sem o código "Dx ·".
                    const materia = block['taxonomy-parent-name'];
                    const topic = stripDescriptorCode(block['video-title']) || stripDescriptorCode(block['taxonomy-name']) || `Bloco ${block.position + 1}`;

                    return (
                      <li
                        key={block.id}
                        style={{ animation: `trilha-in 360ms ${EASE_OUT} ${index * 60}ms both` }}
                        className={cn('relative flex gap-4 pb-3 last:pb-0', playful && 'items-start pb-0', playful && !last && 'lg:min-h-[160px]', playful && index % 2 === 0 && 'lg:pl-[100px]', playful && index % 2 === 1 && 'lg:pr-[100px]')}
                      >
                        {/* rail: node + connector */}
                        <div className="relative flex flex-col items-center">
                          <span
                            className={cn(
                              'z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 text-base font-extrabold transition-colors duration-150 ease-out-strong',
                              playful && 'h-20 w-20 text-2xl',
                              done && 'border-success bg-success text-white',
                              isCurrent && 'border-mission bg-mission text-white',
                              isCurrent && !playful && 'animate-[mission-pulse_3s_ease-in-out_infinite] ring-4 ring-mission-light',
                              !done && !isCurrent && open && 'border-neutral-200 bg-white text-neutral-500',
                              !open && 'border-neutral-100 bg-neutral-50 text-neutral-300'
                            )}
                            style={playful ? { boxShadow: nodeLip } : undefined}
                          >
                            {done ? <FiCheck className="h-5 w-5" /> : !open ? <FiLock className="h-4 w-4" /> : index + 1}
                          </span>
                          {!last &&
                            (playful ? (
                              // Mobile: linha reta simples (no desktop o caminho curvo é o overlay SVG)
                              <span className={cn('w-1 flex-1 rounded-full lg:hidden', done ? 'bg-[#27AE60]' : 'bg-[#E6E1F2]')} />
                            ) : (
                              <span className={cn('w-1 flex-1 rounded-full', done ? 'bg-success' : 'bg-neutral-200')} />
                            ))}
                        </div>

                        {/* card */}
                        <button
                          type="button"
                          data-testid={`block-${block.id}`}
                          disabled={!open}
                          onClick={() => open && handleBlockClick(block)}
                          className={cn(
                            'mb-1 flex min-w-0 flex-1 items-center justify-between gap-3 rounded-2xl border bg-white px-5 py-4 text-left',
                            playful && 'py-6',
                            'transition-[box-shadow,transform,border-color] duration-150 ease-out-strong',
                            open ? 'cursor-pointer shadow-card hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.99]' : 'cursor-default opacity-70',
                            isCurrent ? 'border-mission/60 bg-mission-light/30' : 'border-neutral-100'
                          )}
                        >
                          <span className="min-w-0">
                            <span className="mb-1.5 flex flex-wrap items-center gap-2">
                              {/* Marcação antes do rótulo: 🧠 na revisão, 🎯 no bloco atual */}
                              {(isReview || isCurrent) && (
                                <span
                                  aria-hidden="true"
                                  className="text-xl leading-none"
                                >
                                  {isReview ? '🧠' : '🎯'}
                                </span>
                              )}
                              <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">{isReview ? 'Revisão' : statusLabel({ done, skipped, isCurrent, open })}</span>
                              {materia && (
                                <Badge
                                  variant="muted"
                                  size="sm"
                                >
                                  {materia}
                                </Badge>
                              )}
                            </span>
                            <span className="block truncate text-[15px] font-bold text-neutral-800">{topic}</span>
                          </span>
                          {open && !done && <FiArrowRight className={cn('h-5 w-5 shrink-0', isCurrent ? 'text-mission' : 'text-neutral-300')} />}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}

            {blocks.length > 0 && (
              <div className={cn('mt-2 flex items-center gap-4', playful && 'rounded-[20px] border border-neutral-100 bg-white p-4 shadow-card')}>
                <span className="flex w-12 shrink-0 justify-center">
                  <img
                    src={trophyIcon}
                    alt=""
                    className="h-10 w-10"
                  />
                </span>
                <span className={cn('text-sm font-bold', playful ? 'text-gray-700' : 'text-primary-dark')}>Linha de chegada: complete tudo e a conquista é sua!</span>
              </div>
            )}
          </div>

          {/* Painel do streak (cérebro) — resgatado do legado: gradiente + nível, sticky à direita */}
          <aside className="flex w-full flex-col gap-4 lg:sticky lg:top-[112px] lg:w-[320px] lg:shrink-0">
            {personalized && (
              <ChapterProgressCard
                chapters={trilha.chapters}
                currentId={trilha.id}
              />
            )}
            <StreakPanel
              streak={trilha['streak']}
              continueLabel={personalized ? 'Próxima Missão' : 'Próximo passo'}
              onContinue={currentIndex !== -1 ? () => handleBlockClick(blocks[currentIndex]) : canGenerateNext ? () => setChapterDialog('completed') : undefined}
            />
          </aside>
        </div>
      </div>

      {/* Checkpoint de revisão: quiz em cartas num modal SOBRE a trilha (não navega pro player). */}
      <Dialog
        open={!!reviewState}
        onOpenChange={o => {
          if (!o) setReviewState(null);
        }}
      >
        <DialogContent className="sm:max-w-[680px]">
          <DialogTitle className="text-lg font-bold text-neutral-800">Revisão 🧠 — Checkpoint</DialogTitle>
          {reviewState === 'loading' ? (
            <div className="py-12">
              <Loader />
            </div>
          ) : reviewState ? (
            <div className="pt-2">
              <ScormQuizCards
                cards={reviewState.cards}
                scorm={{ id: reviewState.sectionId }}
                setScormSectionDone={markReviewSectionDone}
                onComplete={finishReview}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <ChapterCompletedDialog
        open={chapterDialog === 'completed'}
        onOpenChange={open => !open && !advancing && setChapterDialog(null)}
        data={chapterCompleted}
        loading={advancing}
        onNext={goToNextChapter}
      />
      {/* Fechar o desempenho também leva ao capítulo novo: o atual já foi concluído. */}
      <PerformanceDialog
        open={chapterDialog === 'performance'}
        onOpenChange={open => !open && startNextChapter()}
        chapter={trilha.chapter}
        nextChapter={nextChapter?.chapter}
        subjects={performance?.areas?.flatMap(area => area.subjects || [])}
        onStart={startNextChapter}
      />
      {/* A trilha concluída sai da lista: fechar volta para as trilhas. */}
      <TrailCompletedDialog
        open={chapterDialog === 'trail-completed'}
        onOpenChange={open => !open && navigate(PATHS.LEARNING_PATH.ROOT)}
        data={trailCompleted}
        onClose={() => navigate(PATHS.LEARNING_PATH.ROOT)}
      />

      <TrilhaPerformanceModal
        trilha={{ title: trailTitle, userLearningPathId: trilha.id }}
        open={viewingPerformance}
        onOpenChange={setViewingPerformance}
      />
    </DashboardContent>
  );
}
